<template>
  <div :class="['Register-Container', isMobile]">
    <h1 class="title">{{ $t('auth.register.welcome') }}</h1>
    <h2 class="subtitle">{{ $t('auth.register.subWelcome') }}</h2>

    <form @submit.prevent="registerUser">
      <vv-form-text-input
        label="username"
        id="username"
        v-model="username"
        :validate="'required|email'"
      />

      <vv-form-text-input
        label="password"
        id="password"
        type="password"
        v-model="password"
        :is-register="true"
        :validate="{ required: true, regex: /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*/ }"
      />

      <vv-form-text-input
        label="confirmPassword"
        id="repeat_password"
        type="password"
        v-model="second_password"
        :validate="{ required: true, is: password }"
      />

      <vv-form-text-input
        v-if="registerFields.firstname !== undefined"
        label="firstname"
        id="firstname"
        v-model="firstname"
        :validate="registerFields.firstname.mandatory ? 'required' : ''"
      />
      <vv-form-text-input
        v-if="registerFields.lastname !== undefined"
        label="lastname"
        id="lastname"
        v-model="lastname"
        :validate="registerFields.lastname.mandatory ? 'required' : ''"
      />

      <vv-form-mask-input
        v-if="registerFields.birthday !== undefined"
        id="birthday"
        label="birthday"
        name="birthday"
        v-model="birthday"
        placeholder="JJ/MM/AAAA"
        :cleave="{date: true, datePattern: ['d', 'm', 'Y'] }"
        :validate="'required|date_format:dd/MM/yyyy|legalAge'"
      />

      <div class="gender-wrapper" v-if="registerFields.gender !== undefined">
        <span class="gender-label">{{ $t('forms.gender.label') }}</span>
        <vv-radio-bubble
          v-for="genderItem in genderList"
          name="genderRadioBubbles"
          :key="genderItem.value"
          :value="genderItem.value"
          :label="genderItem.label"
          v-model="gender"
          :validate="registerFields.gender.mandatory ? 'required' : ''"
        />
      </div>

      <vv-form-text-input
        v-if="registerFields.phone !== undefined"
        label="phone"
        id="phone"
        v-model="phone"
        :validate="registerFields.phone.mandatory ? 'required|numeric' : 'numeric'"
      />

      <vv-form-text-input
        v-if="registerFields.address !== undefined"
        label="address"
        id="address"
        v-model="shippingAddress"
        :validate="registerFields.address.mandatory ? 'required' : ''"
      />

      <vv-form-text-input
        v-if="registerFields.postalCode !== undefined"
        label="postalCode"
        id="postal-code"
        v-model="shippingPostalCode"
        :validate="registerFields.postalCode.mandatory ? 'required|numeric' : 'numeric'"
      />

      <vv-form-checkbox-input label="newsletter" id="newsletter" v-model="newsletter" />

      <span class="Legal-Text" v-html="$t('auth.register.legal')"></span>
      <validate-form-button
        @click="registerUser"
        :isLoading="loading"
        label="auth.register.callToAction"
      />
    </form>

    <span class="Login-Text">{{ $t('auth.login.subWelcome') }}</span>
    <validate-form-button
      v-cypress="$cypress.anonymous.register.button"
      class="ValidateFormButton-Bis"
      @click="$router.push('Login')"
      :isLoading="false"
      :label="'forms.loginCta'"
    />
  </div>
</template>

<script>
import { REGISTER_USER } from '@/store/modules/user/types'
import ValidateFormButton from '@/components/common/forms/vinovibes/ValidateFormButton'
import VvFormTextInput from '@/components/common/forms/vinovibes/VvFormTextInput'
import VvFormMaskInput from '@/components/common/forms/vinovibes/VvFormMaskInput'
import VvFormCheckboxInput from '@/components/common/forms/vinovibes/VvFormCheckboxInput'
import VvRadioBubble from '@/components/common/VvRadioBubble'
import userMixin from '@/mixins/user'
import { SNACKBAR_MESSAGE } from '@/helpers/eventBus'
import * as types from '@/store/modules/user/types'
import { SIGNIN_USER } from '@/store/modules/user/types'
import remoteConfig from '@/lib/remote-config'

const moment = require('moment');

export default {
  name: 'Register',
  mixins: [userMixin],
  components: {
    ValidateFormButton,
    VvFormTextInput,
    VvFormMaskInput,
    VvRadioBubble,
    VvFormCheckboxInput
  },
  data: () => ({
    username: '',
    password: '',
    second_password: '',
    gender: '',
    loading: false,
    firstname: '',
    lastname: '',
    birthday: '',
    phone: '',
    shippingAddress: '',
    shippingPostalCode: '',
    newsletter: false
  }),
  methods: {
    registerUser: async function() {
      const result = await this.$validator.validateAll()
      if (!result) {
        return
      }

      if (this.password !== this.second_password) {
        this.$eventBus.$emit(SNACKBAR_MESSAGE, {
          message: this.$t('errors.custom/password-mismatch')
        })
        return
      }

      if (!this.password.match(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*/) || this.password.length < 8) {
        this.$eventBus.$emit(SNACKBAR_MESSAGE, { message: this.$t('errors.custom/weak-password') })
        return
      }

      if (this.birthday == '' || this.birthday == null) {
        return
      }

      this.loading = true

      const [error, response] = await this.$store.dispatch(REGISTER_USER, {
        username: this.username,
        password: this.password,
        gender: this.gender,
        firstname: this.firstname,
        lastname: this.lastname,
        birthday: this.birthday,
        phone: this.phone,
        shippingAddress: this.shippingAddress,
        shippingPostalCode: this.shippingPostalCode,
        newsletter: this.newsletter
      })
      // this.$logger(error, response)
      if (error || response.data.error) {
        if (error && error.code) {
          console.log('error', error.code)
          this.$eventBus.$emit(SNACKBAR_MESSAGE, { message: this.$t('errors.' + error.code) })
        } else if (response.data.error) {
          this.$eventBus.$emit(SNACKBAR_MESSAGE, {
            message: this.$t('errors.' + response.data.reason)
          })
        } else {
          this.$eventBus.$emit(SNACKBAR_MESSAGE, {
            message: this.$t('errors.custom/internal-error')
          })
        }
      } else {
        this.$gtm.trackEvent({
          event: 'register', // Event type [default = 'interaction'] (Optional)
          category: 'User',
          action: 'create',
          label: 'New register',
          value: {
            username: this.username,
            password: this.password,
            timeStarted: moment().format('l')
          },
          noninteraction: false // Optional
        });
        if (response.data.emailVerified) {
          // Force log the user
          await this.$store.dispatch(SIGNIN_USER, {
            username: this.username,
            password: this.password
          })
        } else {
          this.$store.commit(types.REGISTER_USER, { email: this.username, validated: false })

          this.$router.push({
            name: 'confirm-registration'
          })
        }
      }

      this.loading = false
    }
  },
  computed: {
    isMobile() {
      return this.$mq == 'desktop' ? '' : 'login-mobile'
    },
    registerFields() {
      try {
        return JSON.parse(remoteConfig.getString('register_fields'))
      } catch (error) {
        return {}
      }
    },
    genderList() {
      return [
        { label: this.$t('forms.gender.female'), value: 'female' },
        { label: this.$t('forms.gender.male'), value: 'male' },
        { label: this.$t('forms.gender.unknow'), value: 'unknow' }
      ]
    }
  },
  mounted() {
    if (this.isUserLogged) {
      this.$router.push({ name: 'home' })
      return
    }
  },
  watch: {
    isUserLogged(newState) {
      if (newState) {
        // Automatically logged
        this.$router.push({ name: 'home' })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/_variables.scss';
@import '@/assets/styles/responsive.scss';

.Register-Container {
  display: flex;
  flex-direction: column;
  width: 50%;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
  align-items: center;
  position: relative;
  left: -30px;
  padding-bottom: 100px;

  @include for-size(phone-only) {
    width: 80%;
  }

  &.login-mobile {
    left: 0;
  }

  .title {
    font-size: 60px;
    color: $primary;
    font-weight: 700;
    margin-bottom: 0;
    letter-spacing: -2px;
    text-align: center;

    @include for-size(phone-only) {
      font-size: 2rem;
    }
  }

  .subtitle {
    margin-top: 0;
    margin-bottom: 50px;
    font-family: Caveat;
    color: black;
    font-size: 30px;
    text-align: center;

    @include for-size(phone-only) {
      font-size: 1.5rem;
    }
  }
}

.gender-wrapper {
  margin: 6px 0;
  padding-left: 15px;

  .gender-label {
    text-align: left;
    margin-bottom: 6px;
    font-weight: 500;
    display: block;
  }
}

form {
  background-color: $primary-alt;
  width: 60%;
  padding: 60px 50px 60px 50px;
  border-radius: 30px;

  @include for-size(phone-only) {
    width: 80%;
    padding: 20px 10px 20px 10px;
  }
}

.Login-Text {
  display: block;
  margin-top: 40px;
  margin-bottom: 14px;
  width: 65%;
  font-size: 24px;
  font-weight: 500;
  text-align: center;

  @include for-size(phone-only) {
    font-size: 1rem;
  }
}

.Legal-Text {
  display: block;
  font-size: 10px;
  font-weight: 400;
  text-align: center;
  margin-bottom: 50px;
}

</style>
